import { z } from "zod";
import { windowYearsAreCoherent } from "../drinking-window.js";
import { cellarRoleSchema } from "./cellar.js";
import { bottleFormatSchema, bottleSummarySchema, lotSummarySchema } from "./lot.js";
import { winePhotosField } from "./photo.js";
import { collapseWhitespace, looksMisencoded } from "../text.js";
/** A replacement character means the bytes were already wrong on arrival. */
const MISENCODED = "This text arrived damaged; check the encoding of what sent it.";
/** The kinds this cellar holds. Closed and known, so an enum, not a row. */
export const wineTypeSchema = z.enum([
    "Red",
    "White",
    "Rose",
    "SweetWhite",
    "Dessert",
    "Champagne",
    "Cremant",
    "Sparkling",
]);
const writtenName = z
    .string()
    .transform(collapseWhitespace)
    .pipe(z.string().min(1).max(120))
    .refine((v) => !looksMisencoded(v), { message: MISENCODED });
const optionalName = z
    .string()
    .transform(collapseWhitespace)
    .pipe(z.string().max(120))
    .refine((v) => !looksMisencoded(v), { message: MISENCODED })
    .transform((value) => (value === "" ? null : value))
    .nullable()
    .default(null);
/** ISO 3166-1 alpha-2, as stored. */
const countryCode = z
    .string()
    .transform((value) => value.trim().toUpperCase())
    .pipe(z.string().regex(/^[A-Z]{2}$/));
/**
 * A vintage year, or null. Null means the year is not known; a non-vintage
 * wine is a separate flag, added with the rest of the Champagne fields at 3.3
 * — the database refuses a row claiming both.
 */
const vintage = z.number().int().min(1800).max(2200).nullable().default(null);
/**
 * Producer, region and grapes arrive as names rather than ids. The picker
 * suggests without constraining, so a name that does not exist yet is the
 * normal case and the server resolves or creates the reference row — through
 * the same normaliser the picker matched with.
 */
/**
 * A drinking-window year. Optional like the rest — a wine may be known only
 * vaguely, and three of the four arrive empty from the spreadsheet.
 */
const windowYear = z.number().int().min(1800).max(2200).nullable().default(null);
/**
 * Where a drinking-window year came from. The same three values as the
 * `WindowSource` enum in the schema, and for the same reason: a year the owner
 * judged, a year that arrived with a spreadsheet and a year the owner was
 * shown and accepted are three different claims, and without this they are the
 * same number on the same band.
 *
 * `Suggested` has no writer yet — the route that offers a window and the screen
 * that accepts it are `8.16` and `8.18`. It is declared here and in the schema
 * now because both migrations for this work are additive and are better made
 * once: the deploy that brings the feature then changes no column at all.
 * Unlike the struck `DefaultRule`, this is a value with a dated decision and a
 * numbered task behind it rather than a possibility left standing.
 *
 * It travels out and never in. A request cannot send one — see the note on
 * `createWineRequestSchema` — so this appears on the response schemas alone.
 */
export const windowSourceSchema = z.enum(["Owner", "Import", "Suggested"]);
/**
 * One year's provenance on the way out.
 *
 * Nullable because the year is: the two are null together, which the write
 * paths maintain and `WindowSource` on its own cannot enforce. Defaulted for
 * one release because the browser deploys on a green `main` and the API by
 * hand, so a page that parses this field can reach a phone before the API that
 * sends it.
 *
 * **The default came off on 2026-09-22**, long after that release: the API
 * has sent these since `8.19`. One line here is four fields on every wine and
 * four more wherever a wine is summarised, which is the same economy that
 * made pairing the year with its provenance worth a named schema.
 *
 * Nullable it stays, and that is the whole distinction: a wine nobody has
 * given a drinking window is the common case, and a response with no
 * `drinkFromSource` in it is a broken deploy.
 */
const windowYearSource = windowSourceSchema.nullable();
/** A calendar date with no time in it, as `YYYY-MM-DD`. */
const dateOnly = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .nullable()
    .default(null);
/**
 * Dosage in grams per litre. One decimal, because 9.5 is a real dosage and
 * 9.53 is not a thing anyone writes on a back label.
 */
const dosage = z.number().min(0).max(100).nullable().default(null);
/**
 * Alcohol by volume, as the back label prints it.
 *
 * **Capped at 25 rather than at the column's own 99.99.** The strongest thing
 * a wine cellar holds is a fortified wine — a Port at 20, a Madeira at 22 —
 * and a number above that is not a strong wine, it is `135` typed for `13,5`
 * or a decimal point that went missing. A bound that catches the one realistic
 * mistake is worth more than one that only restates what `Decimal(4, 2)` can
 * hold. Zero is allowed and is a real answer: the cellar has an alcohol-free
 * Juhlin in it.
 *
 * Two decimals, because that is the column, and because 13.5 is what labels
 * say while 12.75 happens.
 */
const alcohol = z.number().min(0).max(25).nullable().default(null);
/**
 * The provenance of each year is not in this schema, and that is deliberate.
 * A year typed into the registration form came from the person typing it, so
 * the server stamps `Owner` on it. Letting the client send a source would let
 * it claim a year came from an authoritative default rule that nobody ran —
 * and recording provenance exists precisely to stop a generated guess being
 * read later as the owner's own opinion.
 */
export const createWineRequestSchema = z
    .object({
    name: writtenName,
    type: wineTypeSchema,
    countryCode,
    producer: writtenName,
    region: optionalName,
    /**
     * The sheet's `Vinstil` — Amarone, Barolo, Champagne. Optional, and
     * expected to be missing often: it is a vocabulary the owner is still
     * filling in, and an empty style says nobody has said yet.
     */
    style: optionalName,
    vintage,
    grapes: z.array(writtenName).max(12).default([]),
    drinkFrom: windowYear,
    peakFrom: windowYear,
    peakTo: windowYear,
    drinkBy: windowYear,
    /**
     * The Champagne fields. For Champagne these are what distinguish two
     * bottles wearing the same label, and a third of this cellar is Champagne.
     */
    isNonVintage: z.boolean().default(false),
    baseVintage: windowYear,
    disgorgedOn: dateOnly,
    dosageGramsPerLitre: dosage,
    /**
     * What the label says the wine is, by volume. Not a Champagne field: every
     * bottle in the cellar carries it, and it is the second-largest thing the
     * record does not know about its own wines — named as such by the meal
     * decision on 2026-09-18 and filled in by the field added on the 19th.
     *
     * Optional like every other field here, and for the reason CLAUDE.md
     * gives: the browser deploys on a green `main` and the API by hand, so a
     * browser that does not know this field yet must still be able to save a
     * wine. Absent means null — the same as every other unanswered field on
     * this form, and what makes the field clearable.
     */
    alcoholPercent: alcohol,
})
    /**
     * A non-vintage wine has no vintage year. The two nullable states of
     * `vintage` mean different things — the sheet writes `-` for non-vintage and
     * leaves the cell blank when the year is simply unrecorded — and this flag is
     * what tells them apart. The database refuses a row claiming both; refusing
     * it here as well means the form can make the state unreachable instead of
     * discovering it in a 400.
     */
    .refine((wine) => !(wine.isNonVintage && wine.vintage !== null), {
    message: "A non-vintage wine has no vintage year.",
    path: ["vintage"],
})
    // The same coherence rule the band draws with, so the browser refuses a peak
    // that ends before it begins without a round trip, and the server refuses it
    // again without trusting that the browser did.
    .refine(windowYearsAreCoherent, {
    message: "The drinking-window years must not run backwards.",
    path: ["peakTo"],
});
export const wineSummarySchema = z.object({
    id: z.string(),
    number: z.number().int(),
    name: z.string(),
    type: wineTypeSchema,
    countryCode: z.string(),
    producer: z.string(),
    region: z.string().nullable(),
    style: z.string().nullable(),
    vintage: z.number().int().nullable(),
    isNonVintage: z.boolean(),
    baseVintage: z.number().int().nullable(),
    disgorgedOn: z.string().nullable(),
    dosageGramsPerLitre: z.number().nullable(),
    /**
     * Additive for one release, per the rule in CLAUDE.md: the browser deploys
     * on a green `main` and the API by hand, so a page that parses this can
     * reach a phone before the API that sends it. A wine with no alcohol
     * recorded is the honest answer from an older API rather than a parse
     * failure at the boundary.
     *
     * **That release ended on 2026-09-21 and the leniency came off two days
     * late**, which is the debt this whole file exists to make visible: the
     * field went out on the 20th and the API that sends it has been serving
     * since revision 41 on the 21st. Nothing was wrong in the meantime — the
     * cost of a default that outlives its release is not a bug, it is that an
     * API which quietly stopped sending the field would be read as a cellar
     * that records no alcohol, and the boundary would fail silently instead of
     * loudly.
     *
     * **Nullable and not optional**, which is the distinction `10.7` restored.
     * A wine with no alcohol recorded is a real state and most of this cellar;
     * a response with no `alcoholPercent` in it is a broken deploy. Only the
     * leniency about the field being absent came off.
     */
    alcoholPercent: z.number().nullable(),
    grapes: z.array(z.string()),
    /**
     * The four years, each beside the note of where it came from. Paired in the
     * schema because they are one fact: a year read without its provenance is
     * exactly the misreading `project-scope.md` recorded the provenance to
     * prevent.
     */
    drinkFrom: z.number().int().nullable(),
    drinkFromSource: windowYearSource,
    peakFrom: z.number().int().nullable(),
    peakFromSource: windowYearSource,
    peakTo: z.number().int().nullable(),
    peakToSource: windowYearSource,
    drinkBy: z.number().int().nullable(),
    drinkBySource: windowYearSource,
});
/**
 * Bottles of one wine standing in one place, counted.
 *
 * Grouped by cabinet and shelf and not by bottle, for the reason the wine's
 * own page groups them: bottles of one wine on one shelf are indistinguishable
 * objects, and the question the list is asked is where to reach, not which one
 * to take.
 *
 * Both names are nullable because a bottle can be in the cellar without being
 * on a shelf — an undo whose shelf was removed leaves one owned and unplaced —
 * and a row that silently dropped those bottles would make a bottle that
 * physically exists invisible in the one view meant to find it.
 */
export const winePlacementSchema = z.object({
    storageName: z.string().nullable(),
    shelfName: z.string().nullable(),
    count: z.number().int(),
});
/**
 * A wine as the list shows it: what it is, and where its bottles are.
 *
 * The placement is on the list row and not on `WineSummary`, because it is not
 * a property of the wine — it is a fact about objects, true this afternoon and
 * possibly false this evening. The wine's own page reads it off the bottles it
 * already has, and the form that registers a wine has no bottles to report, so
 * neither should carry a field that would be empty or duplicated there.
 */
/**
 * A rating as a list row prints it: who said it, what they said, and what the
 * number is out of.
 *
 * Its own shape rather than `wineRatingSchema`, because a list row needs none
 * of the id, the url, the note or the count that the wine's page edits, and
 * this one is carried 121 times in a single response. What it does carry is
 * the scale, for the reason the scale is stored on the source at all: `4,1`
 * and `17` and `92` are all respectable numbers and none of them means
 * anything without the number it is out of.
 *
 * `isOwn` marks the cellar's own voice. The owner is a rating source like any
 * other and appears here under their own name, but the three kinds of score in
 * this domain must not be conflated, so the row is told which one it is
 * holding rather than left to guess from a name.
 */
/**
 * A fact a label can be read for and found not to print. The four of
 * `LabelFact` in `wine-gaps.ts`, which is the rule; this is the wire. Here,
 * above the list row, because the row carries it.
 */
export const labelFactSchema = z.enum([
    "baseVintage",
    "disgorgement",
    "dosage",
    "alcohol",
]);
export const wineListRatingSchema = z.object({
    sourceName: z.string(),
    score: z.number(),
    scaleMax: z.number(),
    scaleHasDecimals: z.boolean(),
    isOwn: z.boolean(),
});
export const wineListRowSchema = wineSummarySchema.extend({
    standing: z.array(winePlacementSchema),
    /**
     * Bottles out of the cellar and coming back. Counted rather than placed,
     * since where they are is a line of free text and not a shelf, and kept
     * separate from the count of what is standing: a bottle at a dinner is owned
     * but is not somewhere you can reach for it.
     */
    away: z.number().int(),
    /**
     * The sizes this wine is still owned in, each named once.
     *
     * Read off the same bottles as `standing` and `away` — what is owned, not
     * what was ever bought — because the question it answers is *have I got a
     * magnum of anything*, and a wine whose only magnum was drunk last year is
     * not an answer to it. A wine with nothing left carries an empty list for
     * the same reason it carries no placement.
     *
     * A list rather than one value, because format belongs to the bottle: the
     * same number is routinely owned in two sizes, and it is the wine that is
     * listed here, not the bottle.
     */
    formats: z.array(bottleFormatSchema),
    /**
     * What anybody thinks of this wine — the latest reading from each source
     * that has one, the cellar's own first.
     *
     * The latest and not all of them: a source read twice is history, and
     * history belongs on the wine's own page. Ordered by the server rather than
     * by the row, because "the cellar's own first" is a fact about which source
     * the cellar points at as its own voice, and the browser has no business
     * knowing that.
     */
    ratings: z.array(wineListRatingSchema),
    /**
     * How many photographs this wine has — a count, not the photographs.
     *
     * The list has never carried anything about photographs, because what makes
     * one expensive to send is its signed URLs and a list of 122 wines needs
     * none of them. The walk through the cabinets asks a question that a count
     * answers completely: *has anybody photographed this bottle*. Uploads that
     * never finished are not counted, for the same reason the wine's own page
     * filters them out — a row with no object behind it is not a photograph.
     *
     * **Nullish on purpose, and it stays that way past its release.** The two
     * nulls are different facts here: nought means the API counted and found
     * none, null means the API did not say. An older API sends nothing, and a
     * browser that read that as nought would summon all 122 wines onto a list
     * whose whole worth is that it is short. So the browser reads null as *not
     * knowable* and does not offer the photograph as something to walk for. It
     * is made required by the same two-step as the rest, once the API that
     * sends it is serving — see `contracts/additive.test.ts`.
     */
    photoCount: z.number().int().nonnegative().nullish().default(null),
    /**
     * The wine's article number at the shop it was bought from, so the list's
     * search can find a wine by the number on a receipt.
     *
     * On the row rather than fetched when a search looks like one, because the
     * search runs in the browser over rows already here, and a second round
     * trip for one field would make the search typed off a receipt the only
     * slow one.
     *
     * It was additive for one release, per CLAUDE.md, and was paid on
     * 2026-09-26: revision 47 had been sending it since the 24th. Null is still
     * an answer — most wines have no number — so only the default came off.
     */
    systembolagetId: z.string().nullable(),
    /**
     * The facts this wine's label was read for and does not print, from `10.24`.
     * *Luckor* walks past them; nothing else reads them.
     *
     * It was additive for one release, per CLAUDE.md, and was paid the same
     * afternoon, once revision 54 was serving it. An empty list stays the answer
     * for a wine nobody has marked; a row without the field is now drift.
     */
    labelSilentOn: z.array(labelFactSchema),
    /**
     * When the wine was last added to, as an ISO instant: the latest of when it
     * was catalogued and when any of its bottles was registered. The list's
     * *Tillagd* order reads it — the decision of 2026-09-26.
     *
     * **The owner's alone, null for every other reader.** A moment that moves
     * each time a lot is bought would tell a guest what the decision on new
     * wines withholds, so the server sends the answer only to the one person it
     * is not news to — the rule `isNew` below is built on, pointing the other
     * way.
     *
     * It was additive for one release, per CLAUDE.md, and was paid the morning
     * it fell due, once revision 51 was serving it. Null is still an answer, for
     * every reader but the owner, so only the default came off.
     */
    addedAt: z.string().nullable(),
    /**
     * Whether this wine was catalogued since the reader last opened the list.
     *
     * **The server decides it, because the reader's bookmark is not something a
     * page should be handed.** That is the rule `10.10` set for a note being
     * new, and it holds harder here: the bookmark now governs a permission, and
     * a browser that could compute *new* from a timestamp it was sent could
     * compute it for somebody who was never granted the indicator.
     *
     * False for the owner on every row, and for any reader not shown this —
     * see `newWines` below, which is the count of the rows this is true of.
     *
     * **Required as of the commit after `10.17`'s deploy.** It defaulted to
     * false for exactly one release, because a browser can reach a phone before
     * the API that feeds it and marking nothing is the right thing to do when
     * nobody has said there is news. That release ended when revision 45 began
     * serving; an absent field is now drift, and drift is supposed to be loud.
     */
    isNew: z.boolean(),
});
export const wineListResponseSchema = z.object({
    wines: z.array(wineListRowSchema),
    /**
     * How many tasting notes somebody else has written that this reader has not
     * seen — the indicator above the list, and nothing else.
     *
     * **It rides on this response rather than being fetched on its own**, which
     * is the argument `cellarListResponseSchema` makes for `offers` and it holds
     * here for the same reason: this is the request the cellar's front page
     * already makes, and a second one for a number that is usually nought would
     * be a second thing to fail on the page somebody opens most. The four other
     * screens that read this list ignore it, which costs them one integer.
     *
     * **Required as of the commit after `10.10`'s deploy**, which is the second
     * half of the additive two-step taken on the day it fell due rather than
     * eleven releases later. It defaulted to nought for exactly one release,
     * because a browser can reach a phone before the API that feeds it and
     * drawing no indicator is the right thing to do when nobody has said there
     * is news. That release is over: the API has been sending this since
     * `e042e3d`, so an absent field is no longer an older API — it is drift, and
     * drift is supposed to be loud.
     */
    unreadNotes: z.number().int().nonnegative(),
    /**
     * How many wines were catalogued since this reader last opened the list.
     *
     * It rides here for `unreadNotes`' reason, word for word: this is the
     * request the cellar's front page already makes, and a second one for a
     * number that is usually nought would be a second thing to fail on the page
     * somebody opens most.
     *
     * **Nought for the owner and for anybody not granted the indicator**, which
     * is why the screen needs no permission of its own to draw with: the
     * resolution happens where the access row is read, the same way `seesPrices`
     * on the cellar summary is the answer rather than the inputs to it. A
     * browser that had the permission would be a browser that could be argued
     * with.
     *
     * **Required as of the commit after `10.17`'s deploy**, on the same terms
     * as `unreadNotes` beside it: it defaulted to nought for one release,
     * because no news is the right thing to draw when nobody has said there is
     * any, and revision 45 ended that release.
     */
    newWines: z.number().int().nonnegative(),
});
/**
 * What the cellar's owner thinks of this wine, on the cellar's own scale.
 *
 * A third kind of score, found at 3.10 and absent from the model until then.
 * The scope had "what somebody else thinks of this wine" and "what I thought
 * of one bottle on one evening"; registering seven real wines produced the
 * same 17 typed onto three identical bottles, because the judgement was about
 * the wine and the only box was on the bottle.
 *
 * It is a reading taken on a date, like every other rating, because what one
 * thinks of a wine legitimately changes as it ages — 17 in 2026 and 15 in 2032
 * are both true, and an application about drinking windows is the last place
 * to pretend otherwise.
 */
export const ownRatingSchema = z.object({
    score: z.number(),
    readOn: z.string(),
});
/** Setting it. The scale is the cellar's and is never sent by the client. */
export const setOwnRatingRequestSchema = z.object({
    /** Null clears the rating for today rather than recording a nought. */
    score: z.number().min(0).max(100).nullable(),
});
/**
 * Somebody else's judgement of this wine, on their own scale.
 *
 * The second of the three kinds of score, and the one that was written and
 * never read: the import brought in 119 of these and no screen could show one.
 * The scale travels with the reading because 4.1 and 17 and 92 are all
 * respectable numbers and none of them means anything without the number it is
 * out of.
 *
 * The date is part of the reading rather than decoration. A Vivino average
 * moves as more people drink the wine, and *4.1 in 2026* stays true after the
 * average has moved on, where a bare *4.1* silently becomes a claim about
 * today.
 */
export const wineRatingSchema = z.object({
    id: z.string(),
    sourceId: z.string(),
    sourceName: z.string(),
    scaleMax: z.number(),
    scaleHasDecimals: z.boolean(),
    score: z.number(),
    readOn: z.string(),
    /** How many people were behind the number, where the source says. */
    ratingCount: z.number().int().nullable(),
    url: z.string().nullable(),
    note: z.string().nullable(),
});
/**
 * Recording one, or correcting it.
 *
 * The source is named rather than chosen by id, exactly as the producer and the
 * grapes are: the cellar's own list is offered, an unfamiliar name creates the
 * row, and the existing spelling wins over a new one that folds to the same
 * thing. `scaleMax` is needed only when the name is new — an existing source
 * already knows what its numbers are out of, and letting a request restate it
 * would be letting a client change what every earlier reading meant.
 *
 * One source on one day is one reading, which is what the schema's unique key
 * has said since `2.6`: saying it again the same day corrects it, and saying it
 * next year adds one.
 */
/**
 * `10.24` — what the label was read for and does not print, as the whole set.
 *
 * The whole set rather than one fact added or taken away, because the row on
 * *Luckor* already holds the set and saying it outright makes saying it twice
 * harmless: a tap repeated on a phone that lost its connection lands on the
 * same answer.
 */
export const setLabelSilenceRequestSchema = z.object({
    facts: z.array(labelFactSchema).max(4),
});
export const labelSilenceResponseSchema = z.object({
    labelSilentOn: z.array(labelFactSchema),
});
export const saveWineRatingRequestSchema = z.object({
    sourceName: z.string().trim().min(1).max(80),
    scaleMax: z.number().int().positive().max(1000).nullable().default(null),
    scaleHasDecimals: z.boolean().default(true),
    score: z.number().min(0).max(1000),
    readOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    ratingCount: z.number().int().positive().max(100_000_000).nullable().default(null),
    // `z.url()` rather than `z.string().url()`, which Zod 4 deprecated in favour
    // of it: the two were checked against nine shapes including the Vivino link
    // this field actually holds, and they accept and refuse exactly the same
    // ones. The same validator, under the name it will still have after the
    // next major.
    url: z.url().max(2000).nullable().default(null),
    note: z.string().max(500).nullable().default(null),
});
/**
 * One bottle, one evening. The third kind of score, and the one that could be
 * written and never read back until now — which broke the promise in
 * `project-scope.md` that a note written five years ago is still findable for a
 * wine no bottle of which remains.
 */
export const tastingNoteSummarySchema = z.object({
    id: z.string(),
    /**
     * The bottle drunk, or null for a wine tasted somewhere else — the decision
     * of 2026-09-26, which made the wine the owner of a note and the bottle the
     * case where there was one.
     *
     * Nullable rather than defaulted, and safe in the order things deploy: the
     * browser, which accepts null, arrives on a green `main` before the API that
     * can first send one is deployed by hand.
     */
    bottleId: z.string().nullable(),
    /**
     * Where it was drunk, for a note without a bottle; null for every bottle's
     * note. Required since 2026-09-29 — defaulted for one release from `10.25`,
     * and every revision since has sent it.
     */
    place: z.string().nullable(),
    tastedOn: z.string(),
    text: z.string(),
    score: z.number().nullable(),
    isFaulty: z.boolean(),
    /**
     * Who wrote it, and whether that is the person reading — both from `9.3`,
     * where a bottle stopped carrying one note and started carrying one per
     * person who was there.
     *
     * The name is null for a note whose author's account has gone: the column is
     * nullable so a note can be anonymised rather than deleted, because the
     * cellar's owner has a real interest in a note written on their own bottle
     * and satisfying somebody else's erasure must not take their record with it.
     *
     * `mine` is the server's answer rather than a name comparison in the
     * browser, because two people may share a name and only one of them may edit
     * a note. Both were defaulted through `9.3`'s release and describe the world
     * before it: every note was the owner's, read by the owner. Required as of
     * `10.7` — `mine` especially, because a default of `true` on a field the API
     * stopped sending would hand somebody else's note an edit button.
     */
    authorName: z.string().nullable(),
    mine: z.boolean(),
});
/**
 * One note as the cellar's own list of them prints it: the note, plus enough
 * of the wine to know what was drunk.
 *
 * Here rather than in a file of its own, beside the note schema it extends and
 * the wine fields it borrows — this is the same idea read from the other end,
 * and splitting it off would put one vocabulary in two places.
 *
 * **Why the list exists.** A note has only ever been readable on the page of
 * the wine it was written on, which answers *what did we think of this wine*
 * and cannot answer *has anybody written anything*. The second question is the
 * one an owner has, and for a cellar shared with guests it is the only way to
 * find out that somebody was here at all.
 *
 * The wine is flattened onto the row rather than nested, because that is what
 * a line of this list prints: number, name, producer and year, the same four
 * things the wine list's own row leads with.
 */
export const cellarNoteSchema = tastingNoteSummarySchema.extend({
    wineId: z.string(),
    wineNumber: z.number().int(),
    wineName: z.string(),
    producer: z.string(),
    vintage: z.number().int().nullable(),
    isNonVintage: z.boolean(),
    /**
     * When the note was written, as distinct from when the bottle was tasted.
     *
     * Both are needed and they are different facts. `tastedOn` is what the note
     * is about and is what the list is ordered by; this is what *new* is decided
     * against, because a note typed up this morning about a dinner last March is
     * news this morning. Ordering by the tasting would bury it.
     */
    writtenAt: z.string(),
    /**
     * What the writer is to this cellar — its owner, or somebody invited in.
     *
     * **A name is not an answer to *who wrote this*.** The list already prints
     * the author, and on a cellar shared with three people that is three names
     * the reader has to know by heart in order to tell their own record from
     * their guests'. The role is the fact actually being asked for.
     *
     * The role rather than a bare *is this a guest* flag, for the reason the
     * role exists at all: `Contributor` is declared and unbuilt, and a boolean
     * would have to be redefined the day somebody is one. Read from the access
     * row, which survives revocation — a note by a guest who has since been
     * removed was still written by a guest.
     *
     * **Nullable, and required as of the commit after its deploy.** The two
     * halves of that sentence are different facts and only one of them was ever
     * temporary. Null stays, because there is genuinely nobody to ask about a
     * note whose author's account has gone — anonymised rather than deleted. The
     * *leniency about the field being absent* was the one-release default, and
     * that release is over: the API has been sending this since `24aad6c`, so a
     * response without it is drift rather than an older API, and the row would
     * quietly print every writer as roleless.
     */
    authorRole: cellarRoleSchema.nullable(),
    /**
     * Whether this arrived since the reader last opened the list.
     *
     * The server's answer rather than a comparison in the browser, for the same
     * reason `mine` is: the reader's own last-looked timestamp is not something
     * a page should be handed and asked to reason about, and two pages reasoning
     * about it would eventually disagree. A note the reader wrote is never new
     * to them.
     */
    isNew: z.boolean(),
});
export const cellarNotesResponseSchema = z.object({
    notes: z.array(cellarNoteSchema),
    /**
     * How many of them are new, counted by the server so that the number on the
     * indicator and the marks in the list cannot tell two different stories.
     */
    unread: z.number().int().nonnegative(),
});
/**
 * Saying what you thought of a bottle somebody else opened.
 *
 * Task `9.3` — **the one deliberate hole in read-only**, and it is there
 * because the bottles most worth having a note on are the ones drunk with
 * somebody else. A guest may write, correct and remove their own note and
 * nothing else; `project-scope.md` has said so since the scope was written.
 *
 * The shape is the owner's own note form, because it is the same act: what it
 * tasted like, out of the cellar's scale, and whether the bottle was faulty.
 * Emptying all three removes the note rather than storing a tasting that
 * produced no opinion — the same rule the departure form already follows.
 */
export const saveTastingNoteRequestSchema = z.object({
    tastedOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    text: z
        .string()
        .transform(collapseWhitespace)
        .pipe(z.string().max(2000))
        .refine((v) => !looksMisencoded(v), { message: MISENCODED }),
    score: z.number().min(0).max(100).nullable().default(null),
    isFaulty: z.boolean().default(false),
});
/**
 * A note about the wine, drunk somewhere else — the decision of 2026-09-26.
 *
 * The bottle note's shape and one more line, *where*, because the bottle that
 * would have said where it came from is not in this cellar. Unlike a bottle's
 * note, emptying it does not delete it: a wine can carry a note per evening,
 * and removing one is its own act with its own button.
 */
export const saveWineNoteRequestSchema = saveTastingNoteRequestSchema.extend({
    place: z
        .string()
        .transform(collapseWhitespace)
        .pipe(z.string().max(120))
        .refine((v) => !looksMisencoded(v), { message: MISENCODED })
        .transform((value) => (value === "" ? null : value))
        .nullable()
        .default(null),
});
/**
 * The window as it stood before the last suggestion was acted on.
 *
 * **Because accepting overwrites.** Ninety-five wines in this cellar carry a
 * single `drinkBy` that arrived with the spreadsheet, and taking a suggestion
 * over one of them leaves no copy of it on the wine. This is that copy: not a
 * second window to reason with, and never drawn as a band — a line of small
 * text saying what used to stand there and where it came from.
 *
 * Null when nothing has been accepted for this wine, which is every wine until
 * somebody presses the button.
 */
export const replacedWindowSchema = z.object({
    drinkFrom: z.number().int().nullable(),
    drinkFromSource: windowSourceSchema.nullable(),
    peakFrom: z.number().int().nullable(),
    peakFromSource: windowSourceSchema.nullable(),
    peakTo: z.number().int().nullable(),
    peakToSource: windowSourceSchema.nullable(),
    drinkBy: z.number().int().nullable(),
    drinkBySource: windowSourceSchema.nullable(),
    /** When the suggestion that replaced it was acted on. */
    replacedAt: z.string(),
});
/**
 * What a suggested window rests on: the producer and vintage, the table of
 * classical appellations, the style in general, the Champagne clock from
 * disgorgement, or the storage temperature — the parts of
 * `drinking-window.md` the model says it used. Asked for on 2026-09-27.
 *
 * Not a list of sources: the model looks nothing up. The reasoning is on the
 * `WindowBasis` enum in the Prisma schema.
 */
export const windowBasisSchema = z.enum([
    "Producer",
    "AppellationTable",
    "Style",
    "Disgorgement",
    "Temperature",
]);
/**
 * One drinking window that was asked for, as the wine's page reads it back.
 *
 * **The reason is why this exists.** The four years it produced are on the wine
 * already; what is not anywhere else is the argument that produced them — *this
 * is appassimento in general, I do not know the producer*, or *2017 was warm and
 * comes forward early*. A year without its argument can only be believed or
 * disbelieved, and in five years nobody will remember which.
 *
 * Dated and attributed, for the same reason `WineRating` carries who said a
 * score and on what scale: in two years the models will be better, and a year
 * that came from this one should say so rather than be read as current.
 *
 * Declined suggestions are here too. *It said 2032 and I said no* is worth as
 * much as the one that was taken — more, sometimes, because it is the reasoning
 * somebody disagreed with.
 */
export const windowSuggestionRecordSchema = z.object({
    id: z.string(),
    askedAt: z.string(),
    actedAt: z.string().nullable(),
    model: z.string(),
    reason: z.string(),
    /**
     * What the answer rested on, and what the cellar told the model — see
     * `windowBasisSchema`. Added 2026-09-27 and required since revision 56
     * sends them. Empty and null are still answers, and true ones, for every
     * suggestion asked before that day: nobody asked for a basis, and nothing
     * kept what was sent.
     */
    basis: z.array(windowBasisSchema),
    told: z.string().nullable(),
    drinkFrom: z.number().int().nullable(),
    peakFrom: z.number().int().nullable(),
    peakTo: z.number().int().nullable(),
    drinkBy: z.number().int().nullable(),
    /** Which of the four became the wine's years unchanged. A year that was
     *  edited before it was taken is false here and `Owner` on the wine. */
    accepted: z.object({
        drinkFrom: z.boolean(),
        peakFrom: z.boolean(),
        peakTo: z.boolean(),
        drinkBy: z.boolean(),
    }),
});
/** One wine with everything hanging off it: its acquisitions and its bottles. */
export const wineDetailSchema = wineSummarySchema.extend({
    lots: z.array(lotSummarySchema),
    bottles: z.array(bottleSummarySchema),
    /** The owner's own latest reading, or null where they have not said. */
    ownRating: ownRatingSchema.nullable(),
    /**
     * Everybody else's readings, newest first — and never the cellar's own
     * source, which is `ownRating` above and would otherwise appear twice on one
     * page saying the same thing in two different shapes.
     */
    ratings: z.array(wineRatingSchema),
    /** Every note written about a bottle of this wine, newest first. They
     *  outlive the bottles: that is what they are for. */
    tastingNotes: z.array(tastingNoteSummarySchema),
    /**
     * What the import said, which never changes however many times the years
     * above are saved.
     *
     * On the detail and deliberately not on the summary: the list sends 123 rows
     * and has no use for it, and this is read on the one page where somebody is
     * judging a single wine. Required as of `10.7`, the release they were
     * defaulted for having long since gone out.
     */
    importedDrinkFrom: z.number().int().nullable(),
    importedPeakFrom: z.number().int().nullable(),
    importedPeakTo: z.number().int().nullable(),
    importedDrinkBy: z.number().int().nullable(),
    /**
     * Every window this wine has been asked about, newest first. Empty for the
     * 118 wines nobody has asked about yet, which is most of them — empty, and
     * sent: required as of `10.7`, where its default was found carrying no note
     * of what it was for, which is the worse half of the same habit.
     */
    windowSuggestions: z.array(windowSuggestionRecordSchema),
    /** What the last accepted suggestion wrote over, or null. Defaulted through
     *  `8.19`'s release so a page could reach a phone before the API that sends
     *  it; required as of `10.7`, now that it has. */
    replacedWindow: replacedWindowSchema.nullable(),
    /**
     * Photographs of the bottle, designated first and then oldest first.
     *
     * Additive for one release for the same reason as `replacedWindow` above:
     * a browser that parses this can reach a phone before the API that sends
     * it, and a wine with no photographs is the honest answer from an older API
     * rather than a parse failure at the boundary.
     */
    photos: winePhotosField,
    /**
     * The article number at Systembolaget, as typed off a receipt — null for
     * most of this cellar, which nobody has typed one for.
     *
     * On the detail and on the list row, not on the summary: the summary is
     * what every write answers with, and none of them has a use for it.
     *
     * **Required since the day it shipped.** It defaulted to null for one
     * release; revision 47 has sent it since the morning of 2026-09-24, and
     * the list search was the next commit, which paid it.
     */
    systembolagetId: z.string().nullable(),
});
/**
 * Changing a wine takes the same shape as registering one, deliberately: the
 * fields that describe a wine do not depend on whether it is new. What is
 * absent is the number — it is printed on labels on bottles standing in a
 * cabinet, so it is not a field anybody edits.
 */
export const updateWineRequestSchema = createWineRequestSchema;
/**
 * A drinking window that was asked for.
 *
 * Four proposed years and the reason they arrived with — and the reason is the
 * part that matters. A number can only be believed or disbelieved; the sentence
 * it came with can be weighed, and it is what says whether the answer rests on
 * knowing this producer or on the category in general.
 *
 * Nothing here has touched the wine. The years become the wine's only when the
 * owner accepts them, one field at a time, which is `8.18`.
 */
export const windowSuggestionSchema = z.object({
    id: z.string(),
    drinkFrom: z.number().int().nullable(),
    peakFrom: z.number().int().nullable(),
    peakTo: z.number().int().nullable(),
    drinkBy: z.number().int().nullable(),
    reason: z.string(),
    /** As on the record above. A suggestion asked now always has both, but the
     *  shape is the record's so one component reads either. */
    basis: z.array(windowBasisSchema),
    told: z.string().nullable(),
    /** Which model answered, and when. In two years the models will be better,
     *  and a year that came from this one should be able to say so. */
    model: z.string(),
    askedAt: z.string(),
});
/**
 * Accepting a suggestion, one field at a time.
 *
 * A field left out is left alone. A field present carries the year that is to
 * stand on the wine — which may be the suggested one or the owner's own edit of
 * it, and **the server decides which by comparing, rather than being told.**
 * A browser that could name the provenance could claim that a year it invented
 * came from a suggestion, and the whole point of the column is that it cannot
 * be claimed.
 *
 * Null is a year being taken away rather than a field being skipped: it is how
 * a suggestion that says *this wine has no useful drinkFrom* is accepted over a
 * year that is already there.
 */
export const acceptWindowSuggestionRequestSchema = z
    .object({
    drinkFrom: windowYear.optional(),
    peakFrom: windowYear.optional(),
    peakTo: windowYear.optional(),
    drinkBy: windowYear.optional(),
})
    .refine((fields) => Object.values(fields).some((year) => year !== undefined), {
    message: "Accepting nothing is not an action.",
});
//# sourceMappingURL=wine.js.map